/** venture/coRemove.ts — 투자법인(회사) 한 줄 지우기 (2026-11-02 지도사 「회사별 진행이 왜 3줄이야 · 하나 지우는 기능」)
 *  원인: 등록정보에서 회사를 지워도 자료수집 쪽 회사 카드(collect.cos)는 그대로 남았다 — 회사별 진행 · 8~15단계는 collect.cos 를 그리므로
 *  같은 회사가 두 줄로 남는다(등록정보에 두 번 적었다가 하나 지운 경우 등).
 *  · removeCoCard: 회사별 진행 · 단계 카드에서 한 줄 지우기 — 회사 카드 + 그 회사 진행값(costep · codone) + 등록정보의 짝(남는 카드보다 많을 때만)
 *  · pruneAfterRegRemove: 등록정보에서 회사를 지울 때 자료수집 쪽 짝도 맞춰 지운다(남는 등록정보보다 많을 때만 · 진행이 적은 카드부터) */
import type { Project, CoCard, Co, Collect } from './types';

const norm = (s: string | undefined): string => (s || '').replace(/\s+/g, '').replace(/주식회사|\(주\)|㈜/g, '').toLowerCase();
const digits = (s: string | undefined): string => (s || '').replace(/\D/g, '');

/** 같은 회사인가 — 사업자번호가 둘 다 있으면 번호로, 아니면 이름(주식회사 · (주) · 띄어쓰기 무시) */
export function sameCo(a: { name?: string; bizno?: string }, b: { name?: string; bizno?: string }): boolean {
  const da = digits(a.bizno); const db = digits(b.bizno);
  if (da && db) return da === db;
  return !!norm(a.name) && norm(a.name) === norm(b.name);
}

/** 이 회사 카드와 같은 회사가 또 있는가(회사별 진행에서 「중복」 표시용) */
export const hasTwin = (cos: CoCard[], co: CoCard): boolean => cos.some((x) => x.id !== co.id && sameCo(x, co));

/** 진행 · 서류가 얼마나 있는지(지울 카드를 고를 때 적은 쪽부터) */
function weight(p: Project, co: CoCard): number {
  const st = (p.costep && p.costep[co.id]) || 0;
  let ups = 0;
  for (let n = 8; n <= 15; n++) {
    const s = (p as unknown as Record<string, { up?: Record<string, string> } | undefined>)['s' + n];
    if (s && s.up) ups += Object.keys(s.up).filter((k) => k.indexOf(co.id + '_') === 0 && s.up && s.up[k]).length;
  }
  return st * 100 + ups + ((p.codone && p.codone[co.id]) ? 10000 : 0);
}

function dropProgress(p: Project, ids: string[]): Pick<Project, 'costep' | 'codone'> {
  const costep = { ...(p.costep || {}) }; const codone = { ...(p.codone || {}) };
  ids.forEach((id) => { delete costep[id]; delete codone[id]; });
  return { costep, codone };
}

/** 회사별 진행 · 단계 카드에서 한 줄 지우기 — 바꿀 값(patch) 또는 null(못 지움) */
export function removeCoCard(p: Project, coId: string): Partial<Project> | null {
  const c = p.collect; if (!c) return null;
  const co = c.cos.find((x) => x.id === coId); if (!co) return null;
  const collect: Collect = { ...c, cos: c.cos.filter((x) => x.id !== coId) };
  const patch: Partial<Project> = { collect, ...dropProgress(p, [coId]) };
  // 등록정보의 짝: 남는 회사 카드 수보다 많으면 하나 지운다(안 지우면 동기화가 카드를 다시 만든다) — 정보가 적은 줄부터
  const reg = p.cos || [];
  const left = collect.cos.filter((x) => sameCo(x, co)).length;
  const mine = reg.map((r, i) => ({ r, i })).filter(({ r }) => sameCo(r, co));
  if (mine.length > left) {
    const filled = (r: Co) => Object.values(r).filter((v) => typeof v === 'string' && v.trim()).length;
    const drop = mine.slice().sort((a, b) => filled(a.r) - filled(b.r) || b.i - a.i)[0];
    patch.cos = reg.filter((_, i) => i !== drop.i);
  }
  return patch;
}

/** 등록정보에서 회사를 지울 때 — 지운 회사와 같은 자료수집 카드가 남는 등록정보보다 많으면 진행이 적은 것부터 지운다 */
export function pruneAfterRegRemove(p: Project, removed: Co, nextReg: Co[]): Partial<Project> {
  const c = p.collect;
  if (!c || !(removed.name || removed.bizno)) return {};
  const keep = nextReg.filter((r) => sameCo(r, removed)).length;
  const cards = c.cos.filter((x) => sameCo(x, removed));
  if (cards.length <= keep) return {};
  const at = (x: CoCard) => c.cos.indexOf(x);
  const drop = cards.slice().sort((a, b) => weight(p, a) - weight(p, b) || at(b) - at(a)).slice(0, cards.length - keep).map((x) => x.id);   // 진행 · 서류가 적은 것 → 같으면 나중에 생긴 것부터
  return { collect: { ...c, cos: c.cos.filter((x) => drop.indexOf(x.id) < 0) }, ...dropProgress(p, drop) };
}
